// NavaidDetail — slide-over detail card for a VOR / VORTAC / NDB / DME.
//
// Surfaces the truthy NASR fields plus a few computed bits the boss
// asked for: GPS coords in three formats and a great-circle bearing
// from the current GPS fix (when available).
//
// We deliberately ape the AirportDetail header layout so the visual
// rhythm matches when the user drills from the long-press picker
// into either kind of feature.

import { useEffect, useState } from 'react';
import { useGpsStore } from '../stores/gpsStore';
import { useTrackStore } from '../stores/trackStore';
import { type PlanPointKind } from '../stores/planStore';
import { greatCircleNm, bearingDeg, trueToMag } from '../lib/geo';
import { formatLatLonDMM, formatLatLonDMS, formatLatLonDD } from '../lib/coords';
import InsertIntoRouteButtons from '../components/route/InsertIntoRouteButtons';
import DetailCard from '../components/DetailCard';

export interface NavaidDetailData {
  ident: string;
  name: string;
  type: string;
  lat: number;
  lon: number;
  freq?: string;
  /** Magnetic variation in degrees east-positive. */
  magVar?: number | null;
  magVarYear?: number | null;
  elevFt?: number | null;
  city?: string;
  state?: string;
}

interface Props {
  navaid: NavaidDetailData;
  onClose: () => void;
}

export default function NavaidDetail({ navaid, onClose }: Props) {
  // Use either the live GPS fix or the most recent track point. If
  // neither is available the bearing/distance rows simply won't render.
  const gpsFix = useGpsStore((s) => s.fix);
  const lastTrack = useTrackStore((s) => s.points[s.points.length - 1]);
  const here = gpsFix ?? lastTrack ?? null;

  // Re-render every 5s so a moving user's bearing-from-here updates.
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 5000);
    return () => clearInterval(t);
  }, []);

  const t = (navaid.type || '').toUpperCase();
  const navaidKind: PlanPointKind = t.includes('NDB') ? 'ndb' : 'vor';

  // Compute bearing/distance from `here` if available.
  let distNm: number | null = null;
  let trueBrg: number | null = null;
  let magBrg: number | null = null;
  if (here) {
    distNm = greatCircleNm(here.lat, here.lon, navaid.lat, navaid.lon);
    const brg = bearingDeg(here.lat, here.lon, navaid.lat, navaid.lon);
    if (Number.isFinite(brg)) {
      trueBrg = brg;
      // Use the navaid's own magvar for the mag bearing — that's what
      // a pilot actually flies to/from when tuning the radial.
      if (navaid.magVar != null) magBrg = trueToMag(brg, navaid.magVar);
    }
  }

  const navaidHeader = (
    <div className="min-w-0">
      <div className="font-mono text-cockpit-100 text-2xl leading-tight">{navaid.ident}</div>
      <div className="text-sm text-cockpit-300 truncate">{navaid.name}</div>
      <div className="text-[11px] text-cockpit-400 truncate">
        {navaid.type}
        {navaid.city ? ` · ${navaid.city}` : ''}
        {navaid.state ? `, ${navaid.state}` : ''}
      </div>
    </div>
  );

  return (
    <DetailCard header={navaidHeader} onClose={onClose}>
      <div className="space-y-3">
        {/* Frequency / type / elevation */}
        <Section title="Identification">
          <Row k="Frequency" v={formatFreq(navaid.freq, navaid.type)} />
          <Row k="Type" v={navaid.type} />
          {navaid.elevFt != null && (
            <Row k="Elevation" v={`${Math.round(navaid.elevFt).toLocaleString()} ft MSL`} />
          )}
          {navaid.magVar != null && (
            <Row
              k="Mag variation"
              v={`${Math.abs(navaid.magVar)}\u00b0${navaid.magVar < 0 ? 'W' : 'E'}${navaid.magVarYear ? ` (${navaid.magVarYear})` : ''}`}
            />
          )}
        </Section>

        {/* GPS in three formats. DMM (Forest Service / fire-aviation
            standard) is at the top so it's the eye-line default per
            Wiley feedback 2026-05-09. */}
        <Section title="Position">
          <Row k="DMM"     v={formatLatLonDMM(navaid.lat, navaid.lon)} mono />
          <Row k="DMS"     v={formatLatLonDMS(navaid.lat, navaid.lon)} mono />
          <Row k="DD"      v={formatLatLonDD(navaid.lat, navaid.lon)}  mono />
        </Section>

        {/* Bearing / distance from here */}
        {here && distNm != null && (
          <Section title="From your position">
            <Row k="Distance" v={`${distNm.toFixed(1)} nm`} />
            {trueBrg != null && (
              <Row k="Bearing (true)" v={`${formatHeading(trueBrg)}\u00b0`} />
            )}
            {magBrg != null && (
              <Row k="Bearing (mag)" v={`${formatHeading(magBrg)}\u00b0`} />
            )}
          </Section>
        )}

        <InsertIntoRouteButtons
          point={{ ident: navaid.ident, lat: navaid.lat, lon: navaid.lon, kind: navaidKind }}
          onDone={onClose}
          className="mt-1"
        />
      </div>
    </DetailCard>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[10px] tracking-widest text-cockpit-300 mb-1">{title.toUpperCase()}</div>
      <div className="bg-cockpit-700/20 border border-cockpit-700 rounded-md px-3 py-2 space-y-0.5">
        {children}
      </div>
    </div>
  );
}

function Row({ k, v, mono }: { k: string; v: string; mono?: boolean }) {
  return (
    <div className="flex items-center justify-between text-xs">
      <span className="text-cockpit-300">{k}</span>
      <span className={(mono ? 'font-mono ' : '') + 'text-cockpit-100'}>{v}</span>
    </div>
  );
}

// Format a NASR navaid frequency. The slim NASR JSON we serve has
// already-formatted strings: VOR family in "NNN.NN" MHz with the
// decimal in place, NDBs in integer kHz. Just append units.
function formatFreq(freq: string | undefined, type: string | undefined): string {
  if (!freq) return '—';
  const t = (type || '').toUpperCase();
  const trimmed = freq.trim();
  if (!trimmed) return '—';
  // NDB: integer kHz (e.g. '258').
  if (t.includes('NDB')) return `${trimmed} kHz`;
  // VOR / VORTAC / VOR-DME / DME / TACAN: MHz already includes the
  // decimal (e.g. '115.80'). If it doesn't, fall back to the older
  // raw-integer format (e.g. '11580' -> '115.80') so we can read
  // legacy data without crashing.
  if (trimmed.includes('.')) return `${trimmed} MHz`;
  const n = Number(trimmed);
  if (!Number.isFinite(n)) return trimmed;
  return `${(n / 100).toFixed(2)} MHz`;
}

// ---- helpers ----
// Coordinate formatters live in src/lib/coords (shared with InfoTab,
// RunwayTab, DetailPane, SettingsPage). DMM is the default per Forest
// Service / fire-aviation standard (Wiley feedback 2026-05-09).

function formatHeading(deg: number): string {
  return Math.round(deg).toString().padStart(3, '0');
}
